import { makeStyles, Tooltip } from '@material-ui/core';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import React from 'react';
import { ArrowDown } from 'react-feather';
import { usePrefixTranslation } from '../../i18n';
import { theme } from '../../theme/theme';
import { Button } from './forms/Button';
import { Select } from './forms/Select';
import tableEmpty from './TableEmpty.svg';

const useTableClasses = makeStyles({
    root: {
        display: 'flex',
    },
    table: {
        display: 'grid',
        overflow: 'auto',
        flex: 1,
        width: 0,
        borderRadius: 4,
        '&::-webkit-scrollbar': { width: 0, height: 0 },
    },
    row: {
        display: 'contents',
        '& > div': {
            backgroundColor: theme.colors.white,
            boxShadow: `0 -1px ${theme.colors.neutral[8]} inset`,
            '&:first-child': { borderRadius: '4px 0 0 4px' },
            '&:last-child': { borderRadius: '0 4px 4px 0' },
        },
        '&:not(.empty).interactive': {
            '&:not(.header)': { cursor: 'pointer' },
            '& > div:first-child': {
                backgroundImage: `linear-gradient(90deg, ${theme.colors.neutral[3]} 4px, white 4px)`,
            },
            '& > div:first-child:hover': {
                backgroundImage: `linear-gradient(90deg, ${theme.colors.darkBlue[5]} 4px, white 4px)`,
            },
        },
    },
    cell: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        '&.fixed': {
            position: 'sticky',
            left: 0,
            zIndex: 1,
            borderRight: `1px solid ${theme.colors.neutral[8]}`,
        },
    },
    header: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        gap: 4,
        height: 38,
        padding: '7px 16px',
        color: theme.colors.neutral[2],
        ...theme.fonts.h6,
        '& > svg': {
            padding: 4,
            borderRadius: '50%',
            color: 'transparent',
            transition: `all ${theme.transitions.default}s`,
        },
        '& > svg:is(.ascending, .descending)': { color: theme.colors.darkBlue[5] },
        '& > svg.descending': { transform: 'rotate(-180deg)' },
        '&:hover > svg': {
            color: theme.colors.neutral[2],
            background: theme.colors.neutral[8],
        },
    },
    placeholder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
    },
    tooltip: {
        padding: 8,
        background: theme.colors.white,
        boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.1)',
        color: theme.colors.neutral[1],
        ...theme.fonts.caption,
    },
    paginator: {
        display: 'flex',
        justifyContent: 'flex-end',
        '& > div': {
            position: 'sticky',
            right: 0,
        },
    },
});

/**
 * Basic table component.
 *
 * @param props.loading any number larger than 0 will indicate loading state, the number itself is the number of rows
 * @param props.columns table columns containing the header element to render, the column size in grid supported units
 *        and if the column is sortable
 * @param props.data table data, the first level of the array are the rows, each element in the second level is a cell
 * @param props.fixed number of fixed columns on the left sie when scrolling
 * @param props.paginator properties required to enable table pagination controls, see Paginator component
 * @param props.onRowClick callback triggered when a row of the table body is clicked
 * @param props.onSortColumn callback triggered when a column sort status is changed, this resets other sorted columns
 */
export const Table = ({
    loading = 0,
    columns,
    cells,
    fixed = 0,
    paginator,
    renderEmpty,
    rowBuilder = () => 'div',
    cellBuilder = () => 'div',
    onRowClick,
    onSortColumn,
    ...rootProps
}: {
    loading?: number;
    columns: { element: React.ReactNode; size?: string; sortable?: boolean }[];
    cells: React.ReactNode[][];
    fixed?: number;
    paginator?: Parameters<typeof Paginator>[0];
    renderEmpty?: React.ReactNode;
    rowBuilder?: (row: number) => string | ((props: JSX.IntrinsicElements['div']) => JSX.Element);
    cellBuilder?: (row: number, column: number) => string | ((props: JSX.IntrinsicElements['div']) => JSX.Element);
    onRowClick?: (index: number) => void;
    onSortColumn?: (index: number, order?: 'ascending' | 'descending') => void;
} & JSX.IntrinsicElements['div']) => {
    const { t } = usePrefixTranslation('global', { global: 'Table' });
    const classes = useTableClasses();
    const orders = [undefined, 'ascending', 'descending'] as const;
    const [sort, setSort] = React.useState<{ column: number; order?: typeof orders[number] }>({ column: 0 });
    const cls = {
        loading: loading !== 0,
        empty: !loading && cells.length === 0,
        interactive: !loading && !!onRowClick,
    };
    const sizes = columns.map(({ size }) => (cls.empty ? '1fr' : size ?? 'auto')).join(' ');
    fixed = cls.empty ? 0 : fixed;

    const header = (
        <div className={clsx(classes.row, cls, 'header')}>
            {columns.map((column, i) => {
                const order = orders[i !== sort.column ? 1 : (orders.indexOf(sort.order) + 1) % 3];
                const title = column.sortable ? t(`sort-${order ?? 'disable'}`) : '';
                const cls = { sortable: column.sortable, fixed: i < fixed, [sort.order ?? '']: i === sort.column };
                return (
                    <Tooltip key={i} classes={{ tooltip: classes.tooltip }} title={title} placement='bottom-start'>
                        <div
                            className={clsx(classes.cell, classes.header, cls)}
                            style={{ gridArea: `1/${1 + i}/auto/auto` }}
                            onClick={() => (setSort({ column: i, order }), onSortColumn?.(i, order))}
                        >
                            {column.element}
                            {column.sortable && <ArrowDown className={clsx(cls)} />}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );

    const content =
        !cls.loading &&
        cells.map((row, i) => {
            const Wrapper = rowBuilder(i);
            return (
                <Wrapper
                    key={i}
                    className={clsx(classes.row, cls)}
                    onClick={e => !e.defaultPrevented && onRowClick?.(i)}
                >
                    {row.map((cell, j) => {
                        const cls = { fixed: j < fixed };
                        const gridArea = `${2 + i}/${1 + j}/auto/auto`;
                        const Wrapper = cellBuilder(i, j);
                        return (
                            <Wrapper key={j} className={clsx(classes.cell, cls)} style={{ gridArea }}>
                                {cell}
                            </Wrapper>
                        );
                    })}
                </Wrapper>
            );
        });

    const skeletons =
        cls.loading &&
        [...Array(loading)].map((_, i) => (
            <div key={i} className={clsx(classes.row, cls)}>
                {[...Array(columns.length)].map((_, j) => {
                    const cls = { fixed: j < fixed };
                    const gridArea = `${2 + i}/${1 + j}/auto/auto`;
                    return (
                        <div key={j} className={clsx(classes.cell, cls)} style={{ gridArea }}>
                            <Skeleton width='100%' />
                        </div>
                    );
                })}
            </div>
        ));

    const placeholder = cls.empty && (
        <div className={clsx(classes.row, cls)}>
            <div className={classes.placeholder} style={{ gridArea: `2/1/2/${columns.length + 1}` }}>
                {renderEmpty ?? <img src={tableEmpty} width={270} height={204} alt='table-empty' />}
            </div>
        </div>
    );

    const tablePaginator = !cls.empty && !!paginator && (
        <div
            className={classes.paginator}
            style={{ gridArea: `${2 + (cls.loading ? loading : cells.length)}/1/auto/${1 + columns.length}` }}
        >
            <Paginator {...paginator} />
        </div>
    );

    return (
        <div {...rootProps} className={clsx(classes.root, rootProps.className)}>
            <div className={classes.table} style={{ gridTemplateColumns: sizes }} role='table'>
                {header}
                {content}
                {skeletons}
                {placeholder}
                {tablePaginator}
            </div>
        </div>
    );
};

const usePaginatorClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.neutral[2],
        ...theme.fonts.label,
        '& > span': { margin: '0 16px' },
    },
    select: {
        background: 'red',
        padding: 9,
        ...theme.fonts.button,
    },
    icon: {
        backgroundColor: 'transparent',
        color: theme.colors.darkBlue[5],
        padding: 8,
        margin: 4,
        '&[disabled]': { color: theme.colors.neutral[3] },
    },
});

/**
 * The table custom paginator.
 *
 * @param props.page the current table page
 * @param props.size the size (number of items) on a page
 * @param props.total total number of elements in the dataset being displayed in the table
 * @param props.sizes possible size options that can be chosen from in the paginator
 * @param props.startCall calls `onPaginationChange` when the component is first rendered
 * @param props.onPaginationChange callback called when a `page` or `size` changes, it also gives an `offset` count
 *        which can be used for fetching paginated data
 */
const Paginator = (props: {
    page: number;
    size: number;
    total: number;
    sizes: number[];
    startCall?: boolean;
    onPaginationChange: (page: number, size: number, offset: number) => void;
}) => {
    const classes = usePaginatorClasses();
    const { t } = usePrefixTranslation('global', { global: 'Table' });
    const pages = props.total > 0 ? Math.ceil(props.total / props.size) : 0;
    const start = 1 + props.page * props.size;
    const end = Math.min(start + props.size - 1, props.total);

    React.useEffect(
        () => void (props.startCall && props.onPaginationChange(props.page, props.size, props.page * props.size)),
        [],
    );

    return (
        <div className={classes.root}>
            <span>{t('rows-per-page')}</span>
            <Select
                insideProps={{ className: classes.select }}
                theme='light'
                options={props.sizes}
                selected={props.sizes.map(s => s === props.size)}
                onSelected={selected => {
                    const size = props.sizes.filter((_, i) => selected[i]).pop() ?? props.size;
                    if (size === props.size) return;
                    const page = Math.floor(props.page * (props.size / size));
                    props.onPaginationChange(page, size, page * size);
                }}
            />
            <span>{t(`page-range`, { start, end, total: props.total })}</span>
            <Button
                className={classes.icon}
                icon
                disabled={props.page === 0}
                onClick={() => props.onPaginationChange(0, props.size, 0)}
                children={<FirstPage />}
            />
            <Button
                className={classes.icon}
                icon
                disabled={props.page === 0}
                onClick={() => props.onPaginationChange(props.page - 1, props.size, (props.page - 1) * props.size)}
                children={<KeyboardArrowLeft />}
            />
            <Button
                className={classes.icon}
                icon
                disabled={props.page === pages - 1}
                onClick={() => props.onPaginationChange(props.page + 1, props.size, (props.page + 1) * props.size)}
                children={<KeyboardArrowRight />}
            />
            <Button
                className={classes.icon}
                icon
                disabled={props.page === pages - 1}
                onClick={() => props.onPaginationChange(pages - 1, props.size, (pages - 1) * props.size)}
                children={<LastPage />}
            />
        </div>
    );
};
